import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { AppEnums } from 'src/app/enums/app.enum';
import { IUser } from 'src/app/models/user.interface';
import { AppService } from 'src/app/services/app.service';
import { environment } from 'src/environments/environment';
import { IValuer, IValuerUnderwriter } from 'src/app/models/valuer.interface';

@Component({
  selector: 'app-valuer-partial',
  templateUrl: './valuer-partial.component.html',
  styleUrls: ['./valuer-partial.component.scss']
})
export class ValuerPartialComponent implements OnInit {

  feduid: string;
  valuer: IValuer;
  user: IUser;
  searchForm: FormGroup;
  underwriterList: IValuerUnderwriter[] = [];
  isLoading: boolean = false;
  sasToken: string;
  userRoles: string[] = [];
  approvedUnderwriterCount: number = 0;
  approveValuerForm: FormGroup;

  constructor(
    private activeRoute: ActivatedRoute,
    public appService: AppService,
    private formBuilder: FormBuilder,
    private router: Router
  ) {
    this.activeRoute.params?.subscribe((p) => {
      this.feduid = p['feduid'];
      this.getValuer(this.feduid);
      this.getPrincipleProfile(this.feduid);
      this.approveValuerForm = this.formBuilder.group({
        commission: [
          '',
          Validators.compose([
            Validators.required,
            Validators.min(1),
            Validators.max(100),
          ]),
        ],
      });
    });
  }

  ngOnInit(): void { }
  underwriterCancelValuerApproval(underwriter: IValuerUnderwriter) {
    this.submitCommission({
      commission: 0.1,
      approved: false,
      underwriterID: underwriter.id,
      valuerID: this.valuer._id,
    });
  }

  underwriterApproveValuer(underwriter: IValuerUnderwriter) {
    if (
      this.approveValuerForm.get('commission').value === 0 ||
      isNaN(this.approveValuerForm.get('commission').value)
    ) {
      this.appService.showToastMessage(
        AppEnums.ToastTypeError,
        'Commission invalid',
        'Make sure you enter a valid commission'
      );
      return;
    }
    let comm = parseFloat(this.approveValuerForm.get('commission').value);
    this.submitCommission({
      commission: comm,
      approved: true,
      underwriterID: underwriter.id,
      valuerID: this.valuer._id,
    });
  }

  submitCommission(data: any) {
    this.isLoading = true;
    this.appService
      .makePostRequest(`${environment.VALUATION_API_URL}/approvevaluer`, data)
      ?.subscribe(
        (response) => {
          this.isLoading = false;
          if (response.Status == 200) {
            this.appService.showToastMessage(
              AppEnums.ToastTypeSuccess,
              response.Status + '',
              response.Message
            );
            this.getValuer(this.valuer.principleFeduid);
          } else {
            this.appService.showToastMessage(
              AppEnums.ToastTypeError,
              'Failed',
              response.Payload
            );
          }
        },
        (err) => {
          this.isLoading = false;
          this.appService.showToastMessage(
            AppEnums.ToastTypeError,
            '',
            this.appService.processHttpErrors(err)
          );
        }
      );
  }

  getValuer(principleFeduid: string) {
    this.isLoading = true;
    this.appService
      .makePostRequest(`${environment.VALUATION_API_URL}/listvaluer`, {
        principleFeduid: principleFeduid,
      })
      ?.subscribe(
        (data) => {
          this.isLoading = false;

          if (data.Status == 200) {
            this.valuer = data.Payload[0];
            this.approvedUnderwriterCount = this.valuer.underwriters.filter(
              (underwriter) => {
                return underwriter.approved;
              }
            ).length;

            if (this.appService.isPartner()) {
              this.underwriterList = this.valuer.underwriters.filter(
                (underwriter) => {
                  return (
                    underwriter.id === this.appService.getSessionUser().partner
                  );
                }
              );
            } else {
              this.underwriterList = this.valuer.underwriters;
            }
          } else {
            this.appService.showToastMessage(
              AppEnums.ToastTypeError,
              'Failed',
              data.Payload
            );
          }
        },
        (err) => {
          this.isLoading = false;

          this.appService.showToastMessage(
            AppEnums.ToastTypeError,
            '',
            this.appService.processHttpErrors(err)
          );
        }
      );
  }

  getPrincipleProfile(feduid: string) {
    this.isLoading = true;
    this.appService
      .makePostRequest(`${environment.CORE_API_URL}/getUserDetailsByFeduid`, {
        feduid: feduid,
        fields: ['userName', 'phone', 'email', 'roles'],
      })
      ?.subscribe((apiResponse) => {
        this.isLoading = false;
        this.user = apiResponse.Payload;
        this.userRoles = this.appService.getRoles(this.user?.roles);
      });
  }

  goToUserProfile() {
    this.router.navigate(['/users/list-client-details', this.feduid]);
  }

  mtekApproveValuer() {
    this.isLoading = true;
    let data = {
      _id: this.valuer.principle_id,
      roles: this.prepareRoles(),
    };
    this.appService
      .makeUpdateRequest(`${environment.CORE_API_URL}/updateRole`, data)
      ?.subscribe(
        (apiResponse) => {
          this.isLoading = false;
          if (apiResponse.Status === 200) {
            this.appService.showToastMessage(
              AppEnums.ToastTypeSuccess,
              apiResponse.Status + '',
              apiResponse.Message
            );
            this.getPrincipleProfile(this.valuer.principleFeduid);
          } else {
            this.appService.showToastMessage(
              AppEnums.ToastTypeError,
              apiResponse.Status + '',
              apiResponse.Payload
            );
          }
        },
        (err) => {
          this.isLoading = true;
          this.appService.showToastMessage(
            AppEnums.ToastTypeError,
            err.status,
            this.appService.processHttpErrors(err)
          );
        }
      );
  }

  get isMtekApproved() {
    return this.userRoles.includes(AppEnums.RoleValuer);
  }

  prepareRoles() {
    let allRoles = [
      {
        name: AppEnums.RoleClient,
        value: true,
      },
      {
        name: AppEnums.RoleValuer,
        value: false,
      },
      {
        name: AppEnums.RolePartner,
        value: false,
      },
      {
        name: AppEnums.RoleAdmin,
        value: false,
      },
      {
        name: AppEnums.RoleSalesAgent,
        value: false,
      },
    ];

    if (this.isMtekApproved) {
      allRoles.push({
        name: AppEnums.RoleValuer,
        value: false,
      });
    } else {
      allRoles.push({
        name: AppEnums.RoleValuer,
        value: true,
      });
      this.underwriterList
        .filter((underwriter) => {
          return (
            underwriter.approved == false ||
            underwriter.approved === undefined ||
            underwriter.approved === null
          );
        })
        .forEach((anUnderwriter) => {
          this.requestUnderwriterApproval(anUnderwriter.id);
        });
    }

    return allRoles;
  }

  requestUnderwriterApproval(insurer: string) {
    this.appService.notifyUnderwriterToApproveValuer(insurer, this.valuer._id);
  }
}
